import { formatCurrency } from '@eriko/shared-types';
import { Scale } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';

interface BalanceSheetAccount {
  name: string;
  balance: number;
}

interface BalanceSheetSection {
  total: number;
  accounts: BalanceSheetAccount[];
}

interface BalanceSheetData {
  type: string;
  asOfDate: string;
  assets: BalanceSheetSection;
  liabilities: BalanceSheetSection;
  equity: BalanceSheetSection;
}

function formatAsOfLabel(asOfDate: string): string {
  try {
    const d = new Date(asOfDate);
    if (Number.isNaN(d.getTime())) return asOfDate;
    return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
  } catch {
    return asOfDate;
  }
}

function AccountRow({ account }: { account: BalanceSheetAccount }) {
  return (
    <div className="group flex items-center py-1.75 pl-11 pr-4.5 text-sm transition-colors hover:bg-surface-50">
      <span className="flex-1 text-surface-700 truncate mr-3">{account.name}</span>
      <span className="font-mono text-[13px] font-medium text-surface-500 text-right tabular-nums shrink-0 transition-colors group-hover:text-surface-800">
        {formatCurrency(account.balance, 2)}
      </span>
    </div>
  );
}

function Section({
  value,
  label,
  total,
  accounts,
}: {
  value: string;
  label: string;
  total: number;
  accounts: BalanceSheetAccount[];
}) {
  return (
    <AccordionItem value={value} className="border-b border-surface-100 last:border-b-0">
      <AccordionTrigger className="flex w-full items-center gap-2 px-4.5 py-2.5 text-left select-none transition-colors hover:bg-surface-50 hover:no-underline">
        <span className="flex-1 text-[13px] font-medium uppercase tracking-[0.04em] text-surface-700">
          {label}
        </span>
        <span className="font-mono text-[13px] font-medium text-surface-700 tabular-nums shrink-0">
          {formatCurrency(total, 2)}
        </span>
      </AccordionTrigger>
      <AccordionContent className="pb-0 pt-0">
        {accounts.map((account) => (
          <AccountRow key={account.name} account={account} />
        ))}
      </AccordionContent>
    </AccordionItem>
  );
}

export function BalanceSheetReport({ data }: { data: Record<string, unknown> }) {
  const bs = data as unknown as BalanceSheetData;
  const asOfLabel = formatAsOfLabel(bs.asOfDate);
  const liabilitiesAndEquity = bs.liabilities.total + bs.equity.total;
  const isBalanced = Math.abs(bs.assets.total - liabilitiesAndEquity) < 0.01;

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <Scale className="h-4.25 w-4.25 text-surface-400" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-surface-900">
            Balance Sheet
          </span>
        </div>
        {bs.asOfDate && (
          <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
            {asOfLabel}
          </span>
        )}
      </div>

      {/* Accordion sections */}
      <Accordion type="multiple" defaultValue={['assets', 'liabilities', 'equity']}>
        <Section
          value="assets"
          label="Assets"
          total={bs.assets.total}
          accounts={bs.assets.accounts}
        />
        <Section
          value="liabilities"
          label="Liabilities"
          total={bs.liabilities.total}
          accounts={bs.liabilities.accounts}
        />
        <Section
          value="equity"
          label="Equity"
          total={bs.equity.total}
          accounts={bs.equity.accounts}
        />
      </Accordion>

      {/* Total Liabilities + Equity footer */}
      <div className="flex items-center border-t border-surface-200 px-4.5 py-2.5 text-sm font-semibold">
        <span className="flex-1 text-surface-900">Total Liabilities & Equity</span>
        <span
          className={`font-mono text-sm font-semibold rounded px-2 py-0.5 ${
            isBalanced
              ? 'text-pos bg-pos-surface'
              : 'text-neg bg-neg-surface'
          }`}
        >
          {formatCurrency(liabilitiesAndEquity, 2)}
        </span>
      </div>
    </div>
  );
}
